import {
  buildStoryTeamBattleRole,
  STORY_BATTLE_ROLE_SOURCE,
} from "./storyHumanCharacterCards";
import { resolveActiveStoryCompanion } from "./storyCompanions";
import { resolveStoryBattleTeam } from "./storyTeamRules";

export const STORY_CAVE_ENCOUNTER_MISSION_ID = "ambient-void-cave";
export const STORY_CAVE_ENCOUNTER_MODE = "roamingCreatureClear";
export const STORY_CAVE_ENCOUNTER_SET_ID = "void-cave-roaming";
export const STORY_CAVE_RESPAWN_MS = 10 * 60 * 1000;

const STORY_CAVE_ENCOUNTER_PREFIX = "void-cave-roam:";
const STORY_CAVE_DEFEATED_STORAGE_KEY = "storyVoidCaveDefeatedEncounters";
const CREATURE_IDS = Object.freeze(["geode", "shroom", "sprout"]);
const memoryDefeatedEncounters = new Set();

export const hashStoryCaveEncounterValue = (value = "") => {
  let hash = 2166136261;
  String(value || "").split("").forEach((character) => {
    hash ^= character.charCodeAt(0);
    hash = Math.imul(hash, 16777619);
  });
  return hash >>> 0;
};

export const getStoryCaveEncounterEpoch = (now = Date.now()) =>
  Math.floor(Number(now || 0) / STORY_CAVE_RESPAWN_MS);

export const buildStoryCaveEncounterId = ({
  sceneKey = "",
  epoch = getStoryCaveEncounterEpoch(),
  slot = 0,
} = {}) =>
  `${STORY_CAVE_ENCOUNTER_PREFIX}${String(sceneKey || "unknown")}:${Number(
    epoch,
  )}:${Number(slot)}`;

export const isStoryCaveEncounterId = (encounterId = "") =>
  String(encounterId || "").startsWith(STORY_CAVE_ENCOUNTER_PREFIX);

export const getStoryCaveEncounterCreatureIds = (encounterId = "") => {
  if (!isStoryCaveEncounterId(encounterId)) return [];

  const seed = hashStoryCaveEncounterValue(encounterId);
  const creatureCount = 1 + (seed % 3);
  return Array.from({ length: creatureCount }, (_, index) =>
    CREATURE_IDS[(seed + index * 7 + (seed >>> (index + 1))) % CREATURE_IDS.length],
  );
};

const readDefeatedEncounterIds = () => {
  if (typeof window === "undefined" || !window.sessionStorage) {
    return new Set(memoryDefeatedEncounters);
  }

  try {
    const stored = JSON.parse(
      window.sessionStorage.getItem(STORY_CAVE_DEFEATED_STORAGE_KEY) || "[]",
    );
    return new Set(Array.isArray(stored) ? stored.map(String) : []);
  } catch (error) {
    return new Set(memoryDefeatedEncounters);
  }
};

const writeDefeatedEncounterIds = (encounterIds) => {
  memoryDefeatedEncounters.clear();
  encounterIds.forEach((encounterId) => memoryDefeatedEncounters.add(encounterId));
  if (typeof window === "undefined" || !window.sessionStorage) return;

  try {
    window.sessionStorage.setItem(
      STORY_CAVE_DEFEATED_STORAGE_KEY,
      JSON.stringify([...encounterIds]),
    );
  } catch (error) {
    // Memory fallback keeps the current game session consistent.
  }
};

export const isStoryCaveEncounterDefeated = (encounterId = "") =>
  readDefeatedEncounterIds().has(String(encounterId || ""));

export const markStoryCaveEncounterDefeated = (encounterId = "") => {
  if (!isStoryCaveEncounterId(encounterId)) return false;
  const defeatedEncounterIds = readDefeatedEncounterIds();
  defeatedEncounterIds.add(String(encounterId));
  writeDefeatedEncounterIds(defeatedEncounterIds);
  return true;
};

export const buildStoryCaveEncounterBattle = ({
  encounterId = "",
  storyState = null,
  world = null,
} = {}) => {
  const creatureIds = getStoryCaveEncounterCreatureIds(encounterId);
  if (!creatureIds.length) return null;

  const { leaderKey, teammateKeys } = resolveStoryBattleTeam(storyState);
  const activeCompanion = resolveActiveStoryCompanion(storyState);

  return {
    launchMode: "direct_match",
    encounterId,
    mode: "teams",
    storySide: {
      sideId: "red",
      leaderRole: buildStoryTeamBattleRole(leaderKey),
      teammateRoles: teammateKeys.map(buildStoryTeamBattleRole),
    },
    opponentSide: {
      sideId: "blue",
      opponentRoles: creatureIds.map((creatureId, index) => ({
        source: STORY_BATTLE_ROLE_SOURCE.STORY_CREATURE,
        characterId: creatureId,
        alias: creatureIds.length > 1 ? `${creatureId}-${index + 1}` : creatureId,
      })),
    },
    roomConfig: {
      amountOfPlayers:
        1 + teammateKeys.length + Math.max(1, creatureIds.length),
      botFill: false,
      public: false,
      ranked: false,
      timedTurns: true,
      turnTime: 30,
      mode: "teams",
      teamCount: 2,
      teamOrder: ["red", "blue"],
    },
    completionPolicy: {
      defaultResultKey: "completed_unknown",
      onResults: {},
    },
    returnTarget: world,
    resumePolicy: {
      allowResumeActiveMatch: true,
      restartIfRoomMissing: true,
    },
    storyCompanionAura: activeCompanion
      ? { companionId: activeCompanion.id }
      : null,
  };
};
